import React, { useCallback, useContext, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button';

import type { FlagsStateContainer } from '@confluence/flags';
import { withFlags } from '@confluence/flags';
import { AtlassianSupportLink, Attribution, isUnauthorizedError } from '@confluence/error-boundary';
import { getMonitoringClient } from '@confluence/monitoring';
import { cfetch } from '@confluence/network';
import {
	ExperienceTrackerContext,
	IMPERSONATION_FLAG_EXPERIENCE,
} from '@confluence/experience-tracker';
import { useSessionData } from '@confluence/session-data';
import { RoutesContext } from '@confluence/route-manager';
import { markErrorAsHandled } from '@confluence/graphql-error-processor';

import { useIsImpersonating } from './useIsImpersonating';

const i18n = defineMessages({
	description: {
		id: 'page-layout.impersonation-flag.description',
		description: 'Default description for impersonation flag when admin is impersonating a user',
		defaultMessage:
			'You’re temporarily logged in as another user. When you’re done, <switchAction>switch back</switchAction> to your account.',
	},
	title: {
		id: 'page-layout.impersonation-flag.title',
		description: 'Default title for impersonation flag when admin is impersonating a user',
		defaultMessage: 'Impersonation active',
	},
	errorTitle: {
		id: 'page-layout.impersonation-flag.error-title',
		description: 'Error title for error flag when API call throws an exception',
		defaultMessage: 'Error',
	},
	errorDescription: {
		id: 'page-layout.impersonation-flag.error-description',
		description: 'Error description for error flag when API call throws an exception',
		defaultMessage:
			'We couldn’t switch back. Try again later, and if this keeps happening, try contacting <atlassianSupport>support</atlassianSupport>.',
	},
});

const ImpersonationFlagComponent = ({ flags }: { flags: FlagsStateContainer }) => {
	const intl = useIntl();
	const { cloudId } = useSessionData();

	const { push } = useContext(RoutesContext);
	const experienceTracker = useContext(ExperienceTrackerContext);

	const stopExperience = useCallback(
		(error: Error) => {
			experienceTracker.stopOnError({
				name: IMPERSONATION_FLAG_EXPERIENCE,
				error,
			});
		},
		[experienceTracker],
	);

	const abortExperience = useCallback(
		(reason: string) => {
			experienceTracker.abort({
				name: IMPERSONATION_FLAG_EXPERIENCE,
				reason,
			});
		},
		[experienceTracker],
	);

	const succeedExperience = useCallback(() => {
		experienceTracker.succeed({
			name: IMPERSONATION_FLAG_EXPERIENCE,
		});
	}, [experienceTracker]);

	const { loading, isImpersonating, targetUserId } = useIsImpersonating({
		cloudId,
		onError: (error) => {
			getMonitoringClient().submitError(error, {
				attribution: Attribution.PERMISSIONS_EXPERIENCE,
			});
		},
	});

	const onReleaseError = useCallback(
		(error: Error) => {
			void flags.showErrorFlag({
				id: 'impersonation-flag-error',
				title: intl.formatMessage(i18n.errorTitle),
				description: intl.formatMessage(i18n.errorDescription, {
					atlassianSupport: (chunks: React.ReactNode[]) => (
						<AtlassianSupportLink>{chunks}</AtlassianSupportLink>
					),
				}),
			});
			// If authorization is being flaky, there is nothing that we can do
			// on the frontend to fix it, so we can just abort the experience and let
			// the user refresh the page to try again.
			if (isUnauthorizedError(error)) {
				markErrorAsHandled(error);
				abortExperience('Unauthorized to release impersonation');
			} else {
				stopExperience(error);
			}
		},
		[flags, intl, stopExperience, abortExperience],
	);

	const dropImpersonation = useCallback(async () => {
		try {
			const response = await cfetch(
				`/gateway/api/adminhub/um/site/${cloudId}/users/${targetUserId}/impersonate/release`,
				{
					method: 'POST',
				},
			);
			const { link } = await response.json();
			succeedExperience();
			push(link);
		} catch (error) {
			onReleaseError(error);
		}
	}, [cloudId, targetUserId, push, succeedExperience, onReleaseError]);

	useEffect(() => {
		if (loading) return;
		if (isImpersonating) {
			experienceTracker.start({
				name: IMPERSONATION_FLAG_EXPERIENCE,
			});
			void flags.showInfoFlag({
				onClose: succeedExperience,
				id: 'impersonation-flag',
				title: intl.formatMessage(i18n.title),
				description: intl.formatMessage(i18n.description, {
					switchAction: (chunks: React.ReactNode[]) => (
						<Button appearance="link" spacing="none" onClick={dropImpersonation}>
							{chunks}
						</Button>
					),
				}),
			});
		}
	}, [
		loading,
		isImpersonating,
		flags,
		intl,
		succeedExperience,
		dropImpersonation,
		experienceTracker,
	]);

	return null;
};

export const ImpersonationFlag = withFlags(ImpersonationFlagComponent);
